import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, KeyRound } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function Settings() {
  const [form, setForm] = useState({ business_name: "", ein: "", email: "", phone: "", address: "", about: "" });
  const [saving, setSaving] = useState(false);
  const [pw, setPwVal] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const setPw = (k) => (e) => setPwVal({ ...pw, [k]: e.target.value });

  const changePw = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) { toast.error("New passwords do not match"); return; }
    if (pw.new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password updated");
      setPwVal({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setPwSaving(false); }
  };

  useEffect(() => { api.get("/settings").then((r) => setForm(r.data)).catch(() => {}); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.put("/settings", form); toast.success("Settings saved"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="settings-page" className="max-w-2xl">
      <h1 className="font-display text-4xl">Business Settings</h1>
      <p className="text-brand-ink/60 mt-2">Your company details, used on invoices and emails.</p>
      <form onSubmit={save} className="mt-8 bg-white rounded-2xl border border-brand-line p-8 space-y-5">
        <Fld label="Business name" v={form.business_name} on={set("business_name")} testid="settings-name" />
        <div className="grid sm:grid-cols-2 gap-5">
          <Fld label="EIN / Business number" v={form.ein} on={set("ein")} testid="settings-ein" />
          <Fld label="Contact phone" v={form.phone} on={set("phone")} testid="settings-phone" />
        </div>
        <Fld label="Contact email" type="email" v={form.email} on={set("email")} testid="settings-email" />
        <Fld label="Address / service area" v={form.address} on={set("address")} testid="settings-address" />
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-brand-ink/50 font-semibold">About</label>
          <textarea value={form.about} onChange={set("about")} rows={3} className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 focus:ring-2 focus:ring-brand-red focus:outline-none" data-testid="settings-about" />
        </div>
        <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-brand-red text-white font-medium hover:bg-brand-reddark transition-colors disabled:opacity-60" data-testid="settings-save-btn"><Save className="w-4 h-4" /> Save settings</button>
      </form>

      <form onSubmit={changePw} className="mt-8 bg-white rounded-2xl border border-brand-line p-8 space-y-5" data-testid="change-password-form">
        <h2 className="font-display text-2xl flex items-center gap-2"><KeyRound className="w-5 h-5 text-brand-red" /> Change Password</h2>
        <p className="text-brand-ink/60 text-sm">Update the password for your owner login. Use at least 8 characters.</p>
        <Fld label="Current password" type="password" v={pw.current_password} on={setPw("current_password")} testid="pw-current" />
        <div className="grid sm:grid-cols-2 gap-5">
          <Fld label="New password" type="password" v={pw.new_password} on={setPw("new_password")} testid="pw-new" />
          <Fld label="Confirm new password" type="password" v={pw.confirm} on={setPw("confirm")} testid="pw-confirm" />
        </div>
        <button type="submit" disabled={pwSaving} className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-brand-ink text-white font-medium hover:bg-black transition-colors disabled:opacity-60" data-testid="change-password-btn"><KeyRound className="w-4 h-4" /> Update password</button>
      </form>
    </div>
  );
}

function Fld({ label, type = "text", v, on, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-brand-ink/50 font-semibold">{label}</label>
      <input type={type} value={v} onChange={on} className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 focus:ring-2 focus:ring-brand-red focus:outline-none" data-testid={testid} />
    </div>
  );
}
